import type { FC } from 'react'
import { useCallback } from 'react'

import { useAutomatedMessageContext } from '@liveHelper/components/AutomatedMessageProvider'
import {
  fakeInput,
  fakeInputGroup,
  fakeSendButton,
} from '@liveHelper/components/MessagePreview/AutomatedMessagePreview.twind'
import {
  MessagePreviewCloseButton,
  MessagePreviewContainer,
  MessagePreviewContent,
  MessagePreviewGroup,
} from '@liveHelper/components/MessagePreview/MessagePreview.styles'
import {
  actionsSelector,
  uiStateSelector,
  useStore,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import {
  useTrackAutomatedMessageImpression,
  useTrackClickAutomatedMessage,
  useTrackDismissAutomatedMessage,
} from '@liveHelper/tracking/useLiveHelperTracking'
import { Close } from '@oneToOne/shared/assets/Close'
import { PaperPlane } from '@oneToOne/shared/assets/PaperPlane'

import { ImpressionContainer } from '@core/components/ImpressionContainer'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'

/**
 * The message preview component on the widget
 */
export const AutomatedMessagePreview: FC = () => {
  const { automatedMessage } = useAutomatedMessageContext()
  const { tw } = useTwindContext()

  const uiState = useStore(uiStateSelector)
  const { dismissAutomatedMessage } = useStore(actionsSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)
  const { chatWithAgent } = useStore(visitorPrivateActionsSelector)

  const trackClickAutomatedMessage = useTrackClickAutomatedMessage()
  const trackDismissAutomatedMessage = useTrackDismissAutomatedMessage()
  const trackAutomatedMessageImpression = useTrackAutomatedMessageImpression()

  const handleClick = useCallback(
    (event) => {
      event.stopPropagation()
      trackClickAutomatedMessage()
      chatWithAgent?.()
    },
    [trackClickAutomatedMessage, chatWithAgent],
  )

  const handleClose = useCallback(
    (event) => {
      event.stopPropagation()
      dismissAutomatedMessage()
      trackDismissAutomatedMessage()
      if (uiState === 'idle_expanded') {
        sendUIStateTransitionEvent('MINIMIZE')
      }
    },
    [
      dismissAutomatedMessage,
      trackDismissAutomatedMessage,
      sendUIStateTransitionEvent,
      uiState,
    ],
  )

  const { t } = useContextTranslation()

  if (!automatedMessage) {
    return null
  }

  return (
    <MessagePreviewGroup onClick={handleClick}>
      <ImpressionContainer trackingFn={trackAutomatedMessageImpression}>
        <MessagePreviewContainer>
          <MessagePreviewCloseButton
            aria-label={t(`oneToOne:Dismiss notification`)}
            actionType="neutral"
            buttonSize="xs"
            shape="circle"
            onClick={handleClose}
            icon={<Close height="8px" width="8px" label="close" />}
          />
          <MessagePreviewContent key={automatedMessage.id}>
            {automatedMessage.message}
          </MessagePreviewContent>
          <div
            className={tw?.(
              fakeInputGroup,
              'rounded',
              'flex',
              'w-full',
              'items-center',
            )}
          >
            <div
              className={tw?.(fakeInput, 'px-3', 'py-1', 'text-sm', 'w-full')}
            >
              {t('oneToOne:Reply')}
            </div>
            <div
              className={tw?.(
                fakeSendButton,
                'flex',
                'justify-center',
                'items-center',
              )}
            >
              <PaperPlane
                aria-hidden={true}
                width="12"
                height="12"
                fill="var(--blk2)"
                label="send"
              />
            </div>
          </div>
        </MessagePreviewContainer>
      </ImpressionContainer>
    </MessagePreviewGroup>
  )
}
